import { ClipboardEvent, KeyboardEvent, useRef, useState } from "react";
import { Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import "./inputs.css";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const SEPARATORS = /[\s,;]+/;

type EmailChipsInputProps = {
  label?: string;
  value: string[];
  onChange: (emails: string[]) => void;
  disabled?: boolean;
  max?: number;
  placeholder?: string;
  helperText?: string;
};

/**
 * Список email-адресов чипами. Адрес добавляется по Enter, запятой, точке с запятой
 * и пробелу, а также при потере фокуса; вставка из буфера режется по тем же разделителям.
 * Невалидный адрес не превращается в чип — остаётся в поле с подсказкой под ним,
 * поэтому наружу через onChange всегда уходит список валидных адресов без повторов.
 */
const EmailChipsInput = ({
  label,
  value,
  onChange,
  disabled = false,
  max = 20,
  placeholder = "inputs.emailChipsPlaceholder"
}: EmailChipsInputProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");

  const commit = (raw: string) => {
    const parts = raw.split(SEPARATORS).filter(Boolean);
    if (parts.length === 0) {
      setDraft("");
      setError("");
      return;
    }

    const next = [...value];
    const invalid: string[] = [];
    let limitReached = false;

    parts.forEach((part) => {
      if (!EMAIL_REGEX.test(part)) {
        invalid.push(part);
        return;
      }
      if (next.some((email) => email.toLowerCase() === part.toLowerCase())) return;
      if (next.length >= max) {
        limitReached = true;
        return;
      }
      next.push(part);
    });

    if (next.length !== value.length) {
      onChange(next);
    }

    setDraft(invalid.join(" "));
    if (invalid.length > 0) {
      setError(t("inputs.emailInvalid", { email: invalid.join(", ") }));
    } else if (limitReached) {
      setError(t("inputs.emailLimit", { max }));
    } else {
      setError("");
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (["Enter", ",", ";", " "].includes(event.key)) {
      // Enter здесь не должен сабмитить форму модалки
      event.preventDefault();
      commit(draft);
      return;
    }
    if (event.key === "Backspace" && draft === "" && value.length > 0) {
      onChange(value.slice(0, -1));
      setError("");
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    commit(`${draft} ${event.clipboardData.getData("text")}`);
  };

  const handleDelete = (email: string) => {
    onChange(value.filter((item) => item !== email));
    setError("");
  };

  return (
    <div className={`email__chips${error ? " error" : ""}`}>
      {label && <div className="email__chips-label">{t(label)}</div>}
      <div
        className={`email__chips-field${disabled ? " disabled" : ""}`}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((email) => (
          <Chip
            key={email}
            label={email}
            size="small"
            className="email__chips-chip"
            onDelete={disabled ? undefined : () => handleDelete(email)}
          />
        ))}
        <input
          ref={inputRef}
          className="email__chips-input"
          type="text"
          value={draft}
          disabled={disabled}
          placeholder={value.length === 0 ? t(placeholder) : ""}
          onChange={(event) => {
            setDraft(event.target.value);
            if (error) setError("");
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => commit(draft)}
        />
      </div>      
    </div>
  );
};

export default EmailChipsInput;
